<template>
  <div class="inputMainBarWrapper">
    <!-- Düzenleme modu bar'ı -->
    <div class="editMessageBar" v-if="isEditMode">
      <div class="editMessageIcon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
        </svg>
      </div>
      <div class="editMessageContent">
        <div class="editMessageTitle">Mesajı düzenle</div>
        <div class="editMessageBody">{{ editingMessageBody }}</div>
      </div>
      <div class="editMessageActions">
        <a class="editMessageCancel" @click="cancelEdit">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </a>
      </div>
    </div>

    <div class="inputMainBar" :style="cssProps">
      <div class="disabledOverlay" v-if="disabled" />
      <a class="hideEmojiKeyboardButton" @click.prevent="$store.state.display.emojiKeyboard.isShown = false" v-if="$store.state.display.emojiKeyboard.isShown">
        <img src="@/assets/images/close4.png" style="width: 30px">
      </a>
      <a class="showEmojiKeyboardButton" @click.prevent="$store.state.display.emojiKeyboard.isShown = true" v-else>
        <img src="@/assets/images/emoji.png" style="width: 30px">
      </a>

      <div class="textInputDiv">
        <div ref="messageInput" contenteditable="true" class="textInputTextArea" :placeholder="inputPlaceholder"
             @keydown="textInputKeydown($event)" @keyup.exact="textInputKeyup($event)" @keyup.enter.exact="handleEnter($event)" @mousedown="currentCaretPosition = getCaretPosition()"
             @mouseup="currentCaretPosition = getCaretPosition()" @input="resizeMessageInput" @paste="onPaste" v-html="inputContent"
        />
        <div v-if="showMentionSuggestions" class="mentionSuggestions">
          <div
            v-for="(user, index) in filteredMentionSuggestions"
            :key="user.UserID"
            ref="mentionSuggestionItems"
            class="mentionSuggestionItem"
            :class="{ active: index === activeMentionIndex }"
            @mousedown.prevent="selectMention(user)"
          >
            <div class="mentionSuggestionName">{{ user.VisibleName }}</div>
            <div class="mentionSuggestionMeta">{{ user.UserID }}</div>
          </div>
        </div>
      </div>

      <div v-if="currentCaretPosition > 0 || isEditMode">
        <a class="showRecordAudioButton" @click="handleSend()">
          <img src="@/assets/images/send.png" style="width: 30px">
        </a>
      </div>
      <div v-else>
        <a class="showRecordAudioButton" @click="showAudioRecorder()" v-if="!$store.state.display.audioInputActive">
          <img src="@/assets/images/microphone.png" style="width: 30px">
        </a>
        <AudioRecorder v-else :class="{ audioInputCollapsed: !$store.state.display.audioInputActive }" />
      </div>
    </div>
  </div>
</template>

<script>
import {
  messageMixin
} from '@/mixins/messageMixin.js'
import {
  socketOutputMixin
} from '@/mixins/socketOutputMixin.js'
import EventBus from '@/eventbus.js'
import {
  setTimeout
} from 'timers'
require('@/security.js')
var htmlToText = require('html-to-text')
var vm
var moment = require('moment')

const AudioRecorder = () => import(/* webpackChunkName: "audio-recorder" */ './AudioRecorder.vue')

export default {
  mixins: [messageMixin, socketOutputMixin],

  data () {
    return {
      writingTimer: null,
      currentCaretPosition: 0,
      messageTemp: null,
      activeMentionIndex: 0,
      mentionQuery: '',
      mentionStartIndex: -1,
      pendingMentionTokens: [],
      lastMentionInputText: '',
      skipNextEnterSend: false
    }
  },

  computed: {
    isChatAttachmentUploadEnabled () {
      return process.env.VUE_APP_CHAT_ATTACHMENT_UPLOAD_ENABLED !== 'false'
    },

    activeConversation () {
      return this.$store.state.conversation.activeConversation
    },

    activeConversationKey () {
      if (!this.activeConversation) {
        return ''
      }

      return this.activeConversation.ConversationID ||
        this.activeConversation.GroupID ||
        this.activeConversation.ChannelID ||
        this.activeConversation.ContactID ||
        ''
    },

    cssProps () {
      return {
        '--inputBarHeight': this.inputBarHeight
      }
    },

    inputBarHeight () {
      if (this.$refs.messageInput !== undefined) {
        return String(this.$refs.messageInput.scrollHeight).concat('px')
      }
      return '18px'
    },

    isUserDeleted () {
      let UserID = this.$store.state.session.user.UserID
      if (this.activeConversation.isGroupConversation && this.activeConversation.Deleteds) {
        if (this.activeConversation.Deleteds.includes(UserID)) {
          return true
        }
      }
      return false
    },

    isUserNotAdmin () {
      let UserID = this.$store.state.session.user.UserID
      if ((this.activeConversation.isGroupConversation || this.activeConversation.isChannelConversation) && this.activeConversation.MessageAuth === 0 && this.activeConversation.Admins) {
        if (this.activeConversation.Owner !== UserID && !this.activeConversation.Admins.includes(UserID)) {
          return true
        }
      }
      return false
    },

    disabled () {
      if (this.isUserDeleted || this.isUserNotAdmin) {
        return true
      } else {
        return false
      }
    },

    inputPlaceholder () {
      if (this.isUserDeleted) {
        return 'Artık bu grupta olmadığınız için mesaj gönderemezsiniz.'
      } else if (this.isUserNotAdmin) {
        return 'Bu gruba sadece yöneticiler mesaj gönderebilir.'
      } else {
        return 'Bir mesaj yazınız...'
      }
    },

    isEditMode () {
      return this.$store.state.message.editingMessage !== null
    },

    editingMessage () {
      return this.$store.state.message.editingMessage
    },

    editingMessageBody () {
      if (this.editingMessage) {
        return this.editingMessage.Body.substring(0, 50) + (this.editingMessage.Body.length > 50 ? '...' : '')
      }
      return ''
    },

    inputContent () {
      if (this.isEditMode && this.editingMessage) {
        return this.editingMessage.Body
      }
      return this.activeConversation.newMessage.Body
    },

    groupMentionCandidates () {
      if (!this.activeConversation || (!this.activeConversation.isGroupConversation && !this.activeConversation.isChannelConversation)) {
        return []
      }

      if (this.activeConversation.isChannelConversation) {
        return []
      }

      let group = this.$store.state.group.groups.find(g => g.GroupID === this.activeConversation.GroupID)
      if (!group) {
        return []
      }

      let myUserID = this.$store.state.session.user.UserID
      let deletedUsers = new Set(group.Deleteds || [])
      let userIDs = []

      let addUser = (userID) => {
        if (!userID || userID === myUserID || deletedUsers.has(userID) || userIDs.includes(userID)) {
          return
        }
        userIDs.push(userID)
      }

      addUser(group.Owner)
      ;(group.Admins || []).forEach(addUser)
      ;(group.Participants || []).forEach(addUser)

      return userIDs
        .map(userID => this.$store.getters.getUserById(userID))
        .filter(user => user && user.VisibleName)
        .sort((a, b) => a.VisibleName.localeCompare(b.VisibleName, 'tr'))
    },

    filteredMentionSuggestions () {
      let query = this.mentionQuery.toLocaleLowerCase('tr')
      return this.groupMentionCandidates.filter(user => {
        if (!query) {
          return true
        }

        return user.VisibleName.toLocaleLowerCase('tr').includes(query) ||
          user.UserID.toLocaleLowerCase('tr').includes(query)
      })
    },

    showMentionSuggestions () {
      return this.mentionStartIndex >= 0 && this.filteredMentionSuggestions.length > 0
    }
  },

  methods: {
    handleEnter (event) {
      if (this.skipNextEnterSend) {
        this.skipNextEnterSend = false
        if (event) {
          event.preventDefault()
        }
        return
      }

      if (this.showMentionSuggestions) {
        let selectedUser = this.filteredMentionSuggestions[this.activeMentionIndex]
        if (selectedUser) {
          this.skipNextEnterSend = true
          this.selectMention(selectedUser)
        }
        if (event) {
          event.preventDefault()
        }
        return
      }

      if (this.isEditMode) {
        this.confirmEdit()
      } else {
        this.addMessageFromMe()
      }
    },

    handleSend () {
      if (this.isEditMode) {
        this.confirmEdit()
      } else {
        this.addMessageFromMe()
      }
    },

    cancelEdit () {
      this.$store.state.message.editingMessage = null
      this.$refs.messageInput.innerHTML = ''
      this.currentCaretPosition = 0
      this.resetMentionInputState()
    },

    confirmEdit () {
      var strippedText = htmlToText.fromString(this.$refs.messageInput.innerHTML, {
        wordwrap: 10030,
        noLinkBrackets: true,
        ignoreHref: true
      })
      var newBody = this.stripHtml(strippedText)
      var mentions = this.mentionsForCurrentInput(newBody)

      if (newBody.trim() === '') {
        return
      }

      // Eğer mesaj değişmediyse düzenlemeyi iptal et
      if (newBody === this.editingMessage.Body && JSON.stringify(mentions) === JSON.stringify(this.editingMessage.Mentions || [])) {
        this.cancelEdit()
        return
      }

      // Mesajı düzenle
      this.editMessage(this.editingMessage, newBody, mentions)
      this.cancelEdit()
    },

    addMessageFromMe () {
      var strippedText = htmlToText.fromString(this.$refs.messageInput.innerHTML, {
        wordwrap: 10030,
        noLinkBrackets: true,
        ignoreHref: true
      })
      this.activeConversation.newMessage.Body = this.stripHtml(strippedText)
      let mentions = this.mentionsForCurrentInput(this.activeConversation.newMessage.Body)
      this.$refs.messageInput.innerHTML = ''
      if (this.activeConversation.newMessage.Body.trim() === '') {
        return
      }
      var newMsg = JSON.parse(JSON.stringify(this.activeConversation.newMessage))
      newMsg.MessageID = this.$uuid.v4()
      newMsg.CreateDate = moment().utc().format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z' // MMMM Do YYYY, h:mm:ss
      newMsg.UpdateDate = moment().utc().format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z' // MMMM Do YYYY, h:mm:ss
      newMsg.From = this.$store.state.session.user.UserID
      if (this.activeConversation.isGroupConversation === true) {
        newMsg.To = this.activeConversation.GroupID
        newMsg.ConversationID = ''
        newMsg.isGroup = true
        newMsg.ConversationType = 1
      } else if (this.activeConversation.isChannelConversation === true) {
        newMsg.To = this.activeConversation.ChannelID
        newMsg.ConversationID = ''
        newMsg.isGroup = false
        newMsg.ConversationType = 2
      } else {
        newMsg.To = this.activeConversation.ContactID
        newMsg.ConversationID = this.activeConversation.ConversationID
        newMsg.isGroup = false
        newMsg.ConversationType = 0
      }
      newMsg.DeleteStatus = 0
      if (mentions.length > 0) {
        newMsg.Mentions = mentions
      }
      // Encrypted message body
      if (this.$store.state.ien) {
        this.$set(newMsg, 'mv', 2) // encryption update
        this.em(newMsg)
      }

      this.$store.dispatch('appendMessages', [newMsg])
      this.$store.dispatch('clearActiveNewMessage')

      // There are controls required to be done for offline/online status and isGroup property while sending messages
      // So it is moved over to the messagemixins

      this.sendMessage(newMsg)
      if (this.writingTimer !== null) {
        this.setStopTyping()
        clearTimeout(this.writingTimer)
        this.writingTimer = null
      }

      EventBus.$emit('scrollToBottom')
      this.currentCaretPosition = 0
      this.resetMentionInputState()
      this.resizeMessageInput()
    },

    textInputKeydown (event) {
      if (this.showMentionSuggestions) {
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          this.activeMentionIndex = (this.activeMentionIndex + 1) % this.filteredMentionSuggestions.length
          this.$nextTick(() => this.scrollActiveMentionIntoView())
          return
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault()
          this.activeMentionIndex = (this.activeMentionIndex - 1 + this.filteredMentionSuggestions.length) % this.filteredMentionSuggestions.length
          this.$nextTick(() => this.scrollActiveMentionIntoView())
          return
        }

        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault()
          let selectedUser = this.filteredMentionSuggestions[this.activeMentionIndex]
          if (selectedUser) {
            this.skipNextEnterSend = true
            this.selectMention(selectedUser)
          }
          return
        }

        if (event.key === 'Escape') {
          this.resetMentionState()
        }
      }

      let message = this.$refs.messageInput.innerHTML
      this.messageTemp = message
      this.currentCaretPosition = this.getCaretPosition()

      if (!this.activeConversation.isChannelConversation && this.writingTimer === null) {
        this.setStartTyping()
      }
      // else {
      //   clearTimeout(this.writingTimer)
      // }

      this.writingTimer = setTimeout(function () {
        vm.timeoutCheck(message)
      }, 2500)
    },

    timeoutCheck (timerMessage) {
      if (timerMessage === this.messageTemp) {
        this.setStopTyping()
        vm.$store.state.message.currentMessageText = vm.$refs.messageInput.innerHTML
        this.writingTimer = null
      }
    },

    showAudioRecorder () {
      this.$store.dispatch('toggleAudioRecorderState')
    },

    textInputKeyup (event) {
      this.currentCaretPosition = this.getCaretPosition()
      this.updateMentionSuggestions(event)
      this.resizeMessageInput()
    },

    resizeMessageInput () {
      this.$nextTick(() => {
        if (!this.$refs.messageInput) {
          return
        }

        const input = this.$refs.messageInput
        const maxHeight = 150

        input.style.height = 'auto'
        input.style.height = String(Math.min(input.scrollHeight, maxHeight)).concat('px')
        input.style.overflowY = input.scrollHeight > maxHeight ? 'auto' : 'hidden'
      })
    },

    htmlEntities (str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },
    stripHtml (html) {
      var tmp = document.createElement('DIV')
      tmp.innerHTML = html
      return tmp.textContent || tmp.innerText || ''
    },

    getCaretPosition () {
      let editableDiv = this.$refs.messageInput
      if (!editableDiv) {
        return 0
      }

      let caretPos = 0
      let selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) {
        return caretPos
      }

      let range = selection.getRangeAt(0)
      let preCaretRange = range.cloneRange()
      preCaretRange.selectNodeContents(editableDiv)
      preCaretRange.setEnd(range.endContainer, range.endOffset)
      caretPos = preCaretRange.toString().length

      return caretPos
    },

    setCaretPosition (position) {
      let editableDiv = this.$refs.messageInput
      if (!editableDiv) {
        return
      }

      let selection = window.getSelection()
      let range = document.createRange()
      let textNode = editableDiv.firstChild

      if (!textNode) {
        range.selectNodeContents(editableDiv)
        range.collapse(false)
      } else {
        range.setStart(textNode, Math.min(position, textNode.textContent.length))
        range.collapse(true)
      }

      selection.removeAllRanges()
      selection.addRange(range)
    },

    resetMentionState () {
      this.mentionQuery = ''
      this.mentionStartIndex = -1
      this.activeMentionIndex = 0
      this.lastMentionInputText = this.$refs.messageInput ? (this.$refs.messageInput.innerText || '') : this.lastMentionInputText
    },

    escapeRegExp (value) {
      return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    },

    updateMentionTokens (previousText, newText) {
      let oldLength = previousText.length
      let newLength = newText.length
      if (oldLength === 0 && newLength === 0) {
        this.pendingMentionTokens = []
        return
      }

      let prefix = 0
      while (prefix < oldLength && prefix < newLength && previousText.charAt(prefix) === newText.charAt(prefix)) {
        prefix++
      }

      let suffix = 0
      while (suffix + prefix < oldLength &&
        suffix + prefix < newLength &&
        previousText.charAt(oldLength - suffix - 1) === newText.charAt(newLength - suffix - 1)) {
        suffix++
      }

      let oldChangedRange = {
        location: prefix,
        length: oldLength - prefix - suffix
      }
      let delta = newLength - oldLength

      this.pendingMentionTokens = this.pendingMentionTokens.map(token => {
        let updated = { ...token }
        if (token.range.location >= oldChangedRange.location + oldChangedRange.length) {
          updated.range.location += delta
        } else {
          let tokenEnd = token.range.location + token.range.length
          let changeEnd = oldChangedRange.location + oldChangedRange.length
          let intersection = Math.max(0, Math.min(tokenEnd, changeEnd) - Math.max(token.range.location, oldChangedRange.location))
          if (intersection > 0) {
            return null
          }
        }
        return this.isMentionTokenValid(updated, newText) ? updated : null
      }).filter(Boolean)
    },

    isMentionTokenValid (token, text) {
      if (!token || token.range.location < 0 || token.range.length <= 0 || token.range.location + token.range.length > text.length) {
        return false
      }

      return text.substring(token.range.location, token.range.location + token.range.length) === '@' + token.displayName
    },

    mentionsForCurrentInput (text) {
      if (!this.activeConversation.isGroupConversation) {
        return []
      }

      let usedRanges = []
      let resolvedTokens = []
      let orderedTokens = [...this.pendingMentionTokens].sort((a, b) => a.range.location - b.range.location)

      orderedTokens.forEach(token => {
        let mentionText = '@' + token.displayName
        let start = -1

        if (this.isMentionTokenValid(token, text)) {
          start = token.range.location
        } else {
          let searchFrom = Math.max(0, token.range.location || 0)
          let candidateStarts = []
          let preferredStart = text.indexOf(mentionText, searchFrom)
          if (preferredStart >= 0) {
            candidateStarts.push(preferredStart)
          }

          let firstStart = text.indexOf(mentionText)
          if (firstStart >= 0 && !candidateStarts.includes(firstStart)) {
            candidateStarts.push(firstStart)
          }

          let scanStart = 0
          while (scanStart < text.length) {
            let foundAt = text.indexOf(mentionText, scanStart)
            if (foundAt < 0) {
              break
            }
            if (!candidateStarts.includes(foundAt)) {
              candidateStarts.push(foundAt)
            }
            scanStart = foundAt + mentionText.length
          }

          candidateStarts.sort((left, right) => {
            return Math.abs(left - searchFrom) - Math.abs(right - searchFrom)
          })

          for (const candidateStart of candidateStarts) {
            let candidateEnd = candidateStart + mentionText.length
            let overlaps = usedRanges.some(range => candidateStart < range.end && candidateEnd > range.start)
            if (!overlaps) {
              start = candidateStart
              break
            }
          }
        }

        if (start < 0) {
          return
        }

        let end = start + mentionText.length
        let overlaps = usedRanges.some(range => start < range.end && end > range.start)
        if (overlaps) {
          return
        }

        usedRanges.push({ start, end })
        resolvedTokens.push({
          ...token,
          range: {
            location: start,
            length: mentionText.length
          }
        })
      })

      this.pendingMentionTokens = resolvedTokens

      return [...resolvedTokens]
        .sort((a, b) => a.range.location - b.range.location)
        .map(token => ({
          UserID: token.userId,
          DisplayName: token.displayName,
          Start: token.range.location,
          Length: token.range.length
        }))
    },

    resetMentionInputState () {
      this.pendingMentionTokens = []
      this.lastMentionInputText = ''
      this.resetMentionState()
    },

    loadMentionTokens (message) {
      let body = message && message.Body ? message.Body : ''
      let mentions = Array.isArray(message && message.Mentions) ? message.Mentions : []
      this.pendingMentionTokens = mentions.map(mention => ({
        userId: mention.UserID,
        displayName: mention.DisplayName || '',
        range: {
          location: mention.Start,
          length: mention.Length
        }
      })).filter(token => this.isMentionTokenValid(token, body))
      this.lastMentionInputText = body
    },

    updateMentionSuggestions (event) {
      if (!this.activeConversation.isGroupConversation || !this.$refs.messageInput) {
        this.resetMentionState()
        return
      }

      let isNavigationKey = event && ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)
      if (isNavigationKey) {
        return
      }

      let body = this.$refs.messageInput.innerText || ''
      this.updateMentionTokens(this.lastMentionInputText, body)
      this.lastMentionInputText = body
      let beforeCaret = body.substring(0, this.currentCaretPosition)
      let match = beforeCaret.match(/(^|\s)@([^\s@]*)$/)

      if (!match) {
        this.resetMentionState()
        return
      }

      this.mentionQuery = match[2] || ''
      this.mentionStartIndex = this.currentCaretPosition - this.mentionQuery.length - 1
      if (!this.showMentionSuggestions) {
        this.activeMentionIndex = 0
      }
    },

    selectMention (user) {
      if (!user || !this.$refs.messageInput) {
        return
      }

      let body = this.$refs.messageInput.innerText || ''
      let caretPosition = this.currentCaretPosition
      let mentionText = '@' + user.VisibleName
      let nextBody = body.substring(0, this.mentionStartIndex) + mentionText + ' ' + body.substring(caretPosition)
      let replacementLength = mentionText.length + 1
      let delta = replacementLength - (caretPosition - this.mentionStartIndex)

      this.pendingMentionTokens = this.pendingMentionTokens.map(token => {
        let updated = { ...token, range: { ...token.range } }
        let tokenEnd = token.range.location + token.range.length
        let replacedEnd = caretPosition

        if (Math.max(token.range.location, this.mentionStartIndex) < Math.min(tokenEnd, replacedEnd)) {
          return null
        }

        if (token.range.location > this.mentionStartIndex) {
          updated.range.location += delta
        }

        return updated
      }).filter(Boolean)

      this.pendingMentionTokens.push({
        userId: user.UserID,
        displayName: user.VisibleName,
        range: {
          location: this.mentionStartIndex,
          length: mentionText.length
        }
      })

      this.$refs.messageInput.innerText = nextBody
      this.$refs.messageInput.focus()

      this.$nextTick(() => {
        let nextPosition = this.mentionStartIndex + mentionText.length + 1
        this.currentCaretPosition = nextPosition
        this.setCaretPosition(nextPosition)
        this.lastMentionInputText = nextBody
        this.resetMentionState()
        this.resizeMessageInput()
      })
    },

    scrollActiveMentionIntoView () {
      let items = this.$refs.mentionSuggestionItems
      if (!items || !items.length) {
        return
      }

      let activeItem = items[this.activeMentionIndex]
      if (activeItem && activeItem.scrollIntoView) {
        activeItem.scrollIntoView({
          block: 'nearest'
        })
      }
    },

    focusInputBar () {
      if (!this.disabled) {
        this.$refs.messageInput.focus()
        this.$refs.messageInput.innerHTML = ''
        this.resetMentionInputState()
      }
    },
    isURL (str) {
      var pattern = new RegExp('^(https?:\\/\\/)?' + // protocol
          '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.?)+[a-z]{2,}|' + // domain name
          '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
          '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
          '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
          '(\\#[-a-z\\d_]*)?$', 'i') // fragment locator
      return pattern.test(str)
    },

    onPaste (event) {
      const clipboardData = event.clipboardData
      if (!clipboardData) {
        this.resizeMessageInput()
        return
      }

      let file = clipboardData.files && clipboardData.files.length > 0
        ? clipboardData.files[0]
        : null

      if (!file && clipboardData.items) {
        for (const item of clipboardData.items) {
          if (item.kind === 'file') {
            file = item.getAsFile()
            if (file) {
              break
            }
          }
        }
      }

      if (file && !this.isChatAttachmentUploadEnabled) {
        event.preventDefault()
        return
      }

      if (file) {
        event.preventDefault()
        file = this.ensureClipboardFileName(file)
        let mod = JSON.parse(
          JSON.stringify(this.$store.state.message.uploadFileModel)
        )
        mod.file = file
        mod.originalFileName = file.name
        mod.processId = this.$uuid.v4()
        mod.uploadUrl = process.env.VUE_APP_UPLOADURL

        if (file.type && file.type.indexOf('image/') === 0) {
          this.$store.commit('setActiveConversationImageUploaderFiles', [mod])
          this.$store.commit('setActiveConversationImageUploaderVisibility', true)
        } else {
          this.$store.commit('setActiveConversationDocumentUploaderFiles', [mod])
          this.$store.commit('setActiveConversationDocumentUploaderVisibility', true)
        }
        return
      }

      this.resizeMessageInput()
    },

    ensureClipboardFileName (file) {
      if (file.name) {
        return file
      }

      const extensionByMimeType = {
        'image/bmp': 'bmp',
        'image/gif': 'gif',
        'image/jpeg': 'jpg',
        'image/png': 'png',
        'image/webp': 'webp'
      }
      const extension = extensionByMimeType[file.type] || 'bin'
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')

      return new File([file], 'ekran-alintisi-' + timestamp + '.' + extension, {
        type: file.type,
        lastModified: Date.now()
      })
    }
  },

  watch: {
    disabled (newVal) {
      if (newVal) {
        this.$refs.messageInput.blur()
        this.$refs.messageInput.innerHTML = ''
        this.resetMentionInputState()
      }
    },
    isEditMode (newVal) {
      if (newVal && this.editingMessage) {
        this.$nextTick(() => {
          this.$refs.messageInput.innerHTML = this.editingMessage.Body
          this.$refs.messageInput.focus()
          this.currentCaretPosition = this.editingMessage.Body.length
          this.loadMentionTokens(this.editingMessage)
          this.resizeMessageInput()
        })
      } else if (!newVal) {
        this.resetMentionInputState()
        this.resizeMessageInput()
      }
    },
    inputContent () {
      this.$nextTick(() => {
        if (!this.$refs.messageInput) {
          return
        }

        this.currentCaretPosition = (this.$refs.messageInput.innerText || '').length
        this.resetMentionInputState()
        this.resizeMessageInput()
      })
    },
    activeConversationKey () {
      this.$nextTick(() => {
        if (!this.$refs.messageInput) {
          return
        }

        this.$refs.messageInput.innerHTML = this.inputContent || ''
        this.currentCaretPosition = (this.$refs.messageInput.innerText || '').length
        this.resetMentionInputState()
        this.resizeMessageInput()
      })
    },
    showMentionSuggestions (newVal) {
      if (newVal) {
        this.$nextTick(() => this.scrollActiveMentionIntoView())
      }
    }
  },

  created () {
    vm = this

    document.addEventListener(
      'keydown',
        function (event) {
          var esc = event.which === 27
          var nl = event.which === 13
          var el = event.target
          var input = el.nodeName !== 'INPUT' && el.nodeName !== 'TEXTAREA'
          var data = {}

          if (vm.$refs && vm.$refs.messageInput === el) {
            return
          }

          if (input) {
            if (esc) {
            // restore state
            document.execCommand('undo')
            el.focus()
          } else if (nl) {
            if (event.shiftKey === true) {
              return
            }
            if (vm.showMentionSuggestions) {
              vm.skipNextEnterSend = true
              vm.handleEnter(event)
              event.preventDefault()
              return
            }
            // save
            data[el.getAttribute('data-name')] = el.innerHTML
            vm.handleEnter(event)

            el.focus()
            event.preventDefault()
          }
        }
      },
      true
    )
  },

  mounted () {
    EventBus.$on('insertEmoji', function (payLoad) {
      let strippedText = htmlToText.fromString(vm.$refs.messageInput.innerHTML, {
        wordwrap: 10030,
        noLinkBrackets: true,
        ignoreHref: true
      })
      vm.activeConversation.newMessage.Body = vm.stripHtml(strippedText)

      let firstpart = vm.activeConversation.newMessage.Body.substring(0, vm.currentCaretPosition)
      let secondPart = vm.activeConversation.newMessage.Body.substring(vm.currentCaretPosition)

      vm.activeConversation.newMessage.Body = firstpart + payLoad + secondPart
      vm.$refs.messageInput.focus()
      vm.resizeMessageInput()

      setTimeout(() => {
        var el = vm.$refs.messageInput
        var range = document.createRange()
        var sel = window.getSelection()

        range.setStart(el.firstChild, vm.currentCaretPosition + 1)
        range.collapse(true)
        sel.removeAllRanges()
        sel.addRange(range)
        vm.resizeMessageInput()
      }, 10)
    })

    if (this.$store.state.conversation.isFirstLoad) {
      this.focusInputBar()
      EventBus.$on('focusInputBar', this.focusInputBar)
      this.$store.state.conversation.isFirstLoad = false
    } else {
      EventBus.$on('focusInputBar', this.focusInputBar)
    }
  },

  beforeDestroy () {
    EventBus.$off('insertEmoji')
    EventBus.$off('focusInputBar')
  },
  components: {
    AudioRecorder
  }
}

</script>

<style lang="less" scoped>
  .inputMainBarWrapper {
    display: flex;
    flex-direction: column;
  }

  .editMessageBar {
    display: flex;
    align-items: center;
    background-color: #008069;
    padding: 10px 15px;
    color: white;
    gap: 12px;
  }

  .editMessageIcon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .editMessageContent {
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }

  .editMessageTitle {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 2px;
  }

  .editMessageBody {
    font-size: 12px;
    opacity: 0.9;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .editMessageActions {
    display: flex;
    gap: 15px;
    flex-shrink: 0;
  }

  .editMessageCancel,
  .editMessageConfirm {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 5px;
    border-radius: 50%;
    transition: background-color 0.2s;
  }

  .editMessageCancel:hover,
  .editMessageConfirm:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  .disabledOverlay {
    position: absolute;
    background-color: rgba(219, 219, 219, 0.3);
    width: 100%;
    height: 100%;
    top: 0px;
    left: 0px;
    z-index: 9999999;
  }

  .inputMainBar {
    max-height: 170px;
    background-color: #efefee;
    display: flex;
    align-items: flex-end;
    flex-shrink: 0;
    position: relative;
  }

  .hideEmojiKeyboardButton {
    flex-grow: 0;
    flex-shrink: 0;
    padding: 20px;
    width: 30px;
    height: 30px;
    z-index: 99999;
    cursor: pointer;
  }

  .showEmojiKeyboardButton {
    flex-grow: 0;
    flex-shrink: 0;
    padding: 20px;
    width: 30px;
    height: 30px;
    z-index: 999;
    cursor: pointer;
  }

  .showRecordAudioButton {
    flex-grow: 0;
    flex-shrink: 0;
    padding: 20px;
    width: 30px;
    height: 30px;
    cursor: pointer;
    z-index: 1000;
  }

  .textInputDiv {
    margin: 10px;
    flex-grow: 1;
    min-width: 0;
    position: relative;
    border-radius: 25px;
    border: none;
    padding-left: 20px;
    font-size: 16px;
    transition: height 0.3s ease;
    display: flex;
    align-items: center;
    background-color: white;
  }

  .textInputTextArea {
    width: calc(100% - 40px);
    resize: none;
    min-height: 38px;
    max-height: 150px;
    box-sizing: border-box;
    line-height: 18px;
    padding: 10px;
    word-break: break-word;
    white-space: pre-wrap;
    overflow-x: hidden;
    overflow-y: hidden;
    border: none;
  }

  .textInputTextArea::-webkit-scrollbar {
    width: 6px;
  }

  .textInputTextArea::-webkit-scrollbar-track {
    background: transparent;
  }

  .textInputTextArea::-webkit-scrollbar-thumb {
    background-color: #c8c8c8;
    border-radius: 6px;
  }

  .textInputTextArea::-webkit-scrollbar-thumb:hover {
    background-color: #a8a8a8;
  }

  .textInputTextArea:focus {
    outline: none;
  }

  .mentionSuggestions {
    position: absolute;
    left: 0;
    right: 0;
    bottom: calc(100% + 8px);
    max-height: 260px;
    background: #ffffff;
    border: 1px solid #d9d9d9;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
    overflow-y: auto;
    overflow-x: hidden;
    z-index: 1001;
  }

  .mentionSuggestionItem {
    padding: 10px 14px;
    cursor: pointer;
  }

  .mentionSuggestionItem.active,
  .mentionSuggestionItem:hover {
    background: #f3f6fb;
  }

  .mentionSuggestionName {
    font-size: 14px;
    color: #1f1f1f;
  }

  .mentionSuggestionMeta {
    margin-top: 2px;
    font-size: 11px;
    color: #8a8f98;
  }

  .audioInputCollapsed {
    width: 0px !important;
    background-color: aliceblue;
  }

</style>

<style>
  [contenteditable="true"]:empty:before {
    content: attr(placeholder);
    display: block;
    /* For Firefox */
  }

</style>
